import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import {
  ContactoAlta, DatosAlta, DocumentoSolicitud, DomicilioFiscal, fiscalDe, fiscalSinVacios, PIEZAS_FISCAL, EventoSolicitud, PAPELES, PapelContacto,
  Solicitud, SolicitudesAltaService, papelesDe, textoFaltanContactos, textoTecnicoObligatorio,
  CentroBeneficio, telefonoInvalido, AYUDA_TELEFONO,
  TiendaInicial, PersonaTienda,
} from 'src/app/services/workflows/solicitudes-alta.service';
import { normalizarCanales } from '../canales-venta/canales-venta.component';
import { mensajeDeError } from 'src/app/services/workflows/workflows.service';
import { PasoRiel } from '../riel-pasos/riel-pasos.component';
import { CuentaBancariaComercio } from '../cuenta-bancaria/cuenta-bancaria.component';
import { OpcionMenu } from '../kb-menu/kb-menu.component';

/** El servidor responde 400 «No cambió nada…» cuando se guarda lo mismo: para quien avanza no es un error. */
/** El mismo texto que el núcleo (`errorDeTienda`). */
const MSG_CAJA = 'El número de la caja es un número entero entre 1 y 9999.';
const sinCambios = (e: any): boolean => /^No cambió nada/.test(String(e?.error?.error || e?.error?.msg || ''));

/** Los mismos ocho campos que edita «La solicitud» (H-03 de ese contrato): acá se cargan de una vez,
 *  sin toggle de ver/editar — esta pantalla ES la de cargar. */
const CAMPOS_DATOS: Array<keyof DatosAlta> = [
  'nombre_comercial', 'razon_social', 'rif', 'tipo_persona', 'telefono', 'correo', 'descripcion',
];

type ClavePaso = 'datos' | 'canales' | 'contactos' | 'cuenta' | 'tienda' | 'documentos' | 'visita' | 'condiciones' | 'contrato';

/**
 * «Completar mi parte» — ruta `/console/altas/:id/cargar` (CONTRATO-REDISENO-ALTAS.md, pantalla B).
 * El ejecutivo carga datos, contactos, documentos y la visita; el gerente carga las condiciones y,
 * después (contrato CONTRATO-ALTA-AUTOMATICA.md §1), el contrato firmado por el comercio — el
 * documento de código `CONTRATO_FIRMADO`, que el ejecutivo NO ve entre los suyos. (El analista
 * comercial desapareció del workflow: lo que hacía él, hoy lo hace el gerente — sin perfil fijo acá:
 * lo dice `secciones_editables` / `la_llena`, que manda el núcleo.)
 *
 * El paso «condiciones» de quien las carga embebe `app-condiciones-pasos` (plazo · cobros · modelos ·
 * riesgo · revisar), el mismo componente de la «Nueva versión» del comercio, en modo 'propuesta'.
 */
@Component({
  selector: 'app-alta-cargar',
  templateUrl: './alta-cargar.component.html',
  styleUrls: ['./alta-cargar.component.css'],
})
export class AltaCargarComponent implements OnInit, OnDestroy {
  cargando = true;
  error = '';
  noExiste = false;
  errorPaso = '';

  sol: Solicitud | null = null;
  hayComentarioVisita = false;
  PAPELES = PAPELES;

  id = '';
  private sub?: Subscription;

  pasoActual: ClavePaso = 'datos';
  navegando = false;

  // ── Datos del comercio ────────────────────────────────────────────────────
  guardandoDatos = false;
  formDatos: DatosAlta = {};
  /** El domicilio fiscal, en las piezas de la ficha del comercio. */
  formFiscal: Required<DomicilioFiscal> = fiscalDe(null);
  PIEZAS_FISCAL = PIEZAS_FISCAL;
  // Juan, 2026-10-08: los centros de beneficio, del catálogo del núcleo (varios; al menos uno para aprobar).
  centros: CentroBeneficio[] = [];
  formCentros: string[] = [];
  telefonoInvalido = telefonoInvalido;
  AYUDA_TELEFONO = AYUDA_TELEFONO;

  // ── Canales de venta (Juan, 2026-10-23: los marca el ejecutivo; el gerente puede cambiarlos) ─────
  guardandoCanales = false;
  formCanales: string[] = [];

  // ── Contactos ─────────────────────────────────────────────────────────────
  guardandoContactos = false;
  formContactos: ContactoAlta[] = [];

  // ── Cuenta bancaria (CONTRATO-PUESTA-EN-MARCHA.md, «C») ──────────────────────────────────────
  guardandoCuenta = false;
  cuentaActual: CuentaBancariaComercio | null = null;

  // ── Documentos ────────────────────────────────────────────────────────────
  subiendoTipo: string | null = null;
  quitandoTipo: string | null = null;

  // ── La visita ─────────────────────────────────────────────────────────────
  comentarioVisita = '';
  comentando = false;

  // ── Condiciones (fallback: el formulario del comercio embebido, modo 'propuesta') ────────────
  guardandoCondiciones = false;
  errorCondiciones = '';
  editandoCondicionesFallback = false;

  constructor(private _sa: SolicitudesAltaService, private route: ActivatedRoute, private router: Router) { }

  ngOnInit(): void {
    this.sub = this.route.paramMap.subscribe((p) => {
      this.id = p.get('id') || '';
      this.cargar();
    });
  }

  ngOnDestroy(): void { this.sub?.unsubscribe(); }

  // ── Carga ─────────────────────────────────────────────────────────────────

  cargar = async (): Promise<void> => {
    this.cargando = true; this.error = ''; this.noExiste = false; this.errorPaso = '';
    try {
      const [sol, eventos, centros] = await Promise.all([
        this._sa.obtener(this.id), this._sa.historial(this.id), this._sa.centrosBeneficio().catch(() => [] as CentroBeneficio[]),
      ]);
      this.centros = centros;
      this.sol = sol;
      this.hayComentarioVisita = eventos.some((e: EventoSolicitud) => e.tipo === 'COMENTARIO');
      this.prepararFormDatos();
      this.prepararFormCanales();
      this.prepararFormContactos();
      this.cuentaActual = sol.datos.cuenta || null;
      this.prepararFormTienda();
      const pedido = this.route.snapshot.queryParamMap.get('paso') || '';
      const claves = this.pasosDef.map((p) => p.clave);
      const inicial = claves.includes(pedido as ClavePaso) ? (pedido as ClavePaso)
        : (this.pasosDef.find((p) => !this.pasoHecho(p.clave))?.clave || this.pasosDef[0]?.clave || 'datos');
      this.pasoActual = inicial;
    } catch (e: any) {
      this.sol = null;
      this.noExiste = e?.status === 404;
      if (!this.noExiste) this.error = mensajeDeError(e, 'No se pudo traer la solicitud.');
    } finally {
      this.cargando = false;
    }
  };

  // ── Quién entra y a qué (por la sección que trae `secciones_editables`, nunca por un perfil fijo:
  //    el analista comercial desapareció del workflow y hoy es el gerente quien carga esto) ────────

  get cargaCondiciones(): boolean { return !!this.sol?.secciones_editables.includes('CONDICIONES'); }
  get esEjecutivo(): boolean {
    const s = this.sol?.secciones_editables || [];
    return s.includes('DATOS') || s.includes('CONTACTOS') || s.includes('DOCUMENTOS') || s.includes('CUENTA');
  }
  get puedeCargar(): boolean { return !!this.sol?.es_mia && (this.esEjecutivo || this.cargaCondiciones); }
  get tituloParte(): string { return this.cargaCondiciones ? 'Condiciones comerciales' : 'Expediente del comercio'; }

  private readonly PASOS_EJECUTIVO: Array<{ clave: ClavePaso; nombre: string }> = [
    { clave: 'datos', nombre: 'Datos del comercio' },
    // Juan, 2026-10-23: los canales los marca el ejecutivo ANTES de los contactos: así ya se sabe si el técnico es obligatorio.
    { clave: 'canales', nombre: 'Canales de venta' },
    { clave: 'contactos', nombre: 'Contactos' },
    { clave: 'cuenta', nombre: 'Cuenta bancaria' },
    { clave: 'tienda', nombre: 'Tienda y caja' },
    { clave: 'documentos', nombre: 'Documentos' },
    { clave: 'visita', nombre: 'La visita' },
  ];
  private readonly PASOS_CONDICIONES: Array<{ clave: ClavePaso; nombre: string }> = [
    { clave: 'condiciones', nombre: 'Condiciones' },
    { clave: 'contrato', nombre: 'Contrato firmado' },
  ];

  get pasosDef(): Array<{ clave: ClavePaso; nombre: string }> {
    // El gerente comercial también puede cargar contactos (solo agregar uno Técnico o marcárselo a uno existente):
    // si el núcleo le abre CONTACTOS, el editor de contactos va primero.
    // Los canales (los marcó el ejecutivo) los puede cambiar el gerente: van antes de los contactos.
    if (this.cargaCondiciones) {
      const ed = this.sol?.secciones_editables || [];
      const previos: Array<{ clave: ClavePaso; nombre: string }> = [];
      if (ed.includes('CANALES')) previos.push({ clave: 'canales', nombre: 'Canales de venta' });
      if (ed.includes('CONTACTOS')) previos.push({ clave: 'contactos', nombre: 'Contactos' });
      if (previos.length) return [...previos, ...this.PASOS_CONDICIONES];
    }
    return this.cargaCondiciones ? this.PASOS_CONDICIONES : this.PASOS_EJECUTIVO;
  }

  private pasoHecho(clave: ClavePaso): boolean {
    const s = this.sol; if (!s) return false;
    if (clave === 'datos') return !!(s.datos.nombre_comercial && s.datos.razon_social && s.datos.rif && !!fiscalDe(s.datos).street.trim() && !!fiscalDe(s.datos).city.trim()
      && (s.datos.centros_beneficio || []).length);   // Juan, 2026-10-08: sin centros de beneficio no está listo
    if (clave === 'canales') return (s.canales || []).length > 0;
    if (clave === 'contactos') return !textoFaltanContactos(s.contactos.filter((c) => !!c.nombre), s.canales);
    // Hecho = número de 20 dígitos, titular y RIF del titular cargados. Sin eso no frena avanzar
    // (el FRENA es al aprobar, contrato C: `para_aprobar.EJECUTIVO_COMERCIAL` suma CUENTA_BANCARIA).
    if (clave === 'cuenta') {
      const c = s.datos.cuenta;
      return !!(c && /^\d{20}$/.test(c.numero || '') && c.titular && c.rif_titular);
    }
    if (clave === 'tienda') {
      const t = s.datos.tienda;
      // Juan, 2026-10-19: con su gerente (nombre y correo); la caja es un usuario, no pide persona.
      return !!(t && t.codigo && t.nombre && t.nombre_corto && t.caja && t.gerente?.nombre && t.gerente?.correo);
    }
    // Hecho = los cinco del ejecutivo cargados (el contrato es aparte, de quien carga condiciones). Los de «para
    // aprobar» no frenan (piden la nota), pero si falta uno el paso no está hecho: al entrar se cae
    // ahí y no en el último, como si ya estuviera todo.
    if (clave === 'documentos') return this.documentosEjecutivo.every((d) => !!d.cargado);
    if (clave === 'visita') return this.hayComentarioVisita;
    if (clave === 'contrato') return !!this.documentoContrato?.cargado;
    return !!s.condiciones;   // 'condiciones'
  }

  /** Los documentos que carga el ejecutivo en este paso: todos salvo el contrato firmado, que es de
   *  quien carga las condiciones (contrato CONTRATO-ALTA-AUTOMATICA.md §1) y tiene su propio paso
   *  «Contrato firmado». */
  get documentosEjecutivo(): DocumentoSolicitud[] {
    return (this.sol?.documentos || []).filter((d) => d.codigo !== 'CONTRATO_FIRMADO');
  }

  /** El documento `CONTRATO_FIRMADO` de la solicitud, o `null` si este tipo no lo trae. */
  get documentoContrato(): DocumentoSolicitud | null {
    return (this.sol?.documentos || []).find((d) => d.codigo === 'CONTRATO_FIRMADO') || null;
  }

  get pasosRiel(): PasoRiel[] {
    return this.pasosDef.map((p) => ({
      clave: p.clave, nombre: p.nombre,
      estado: p.clave === this.pasoActual ? 'ACTUAL' : (this.pasoHecho(p.clave) ? 'HECHO' : 'PENDIENTE'),
    }));
  }

  get indexActual(): number { return this.pasosDef.findIndex((p) => p.clave === this.pasoActual); }
  get esPrimerPaso(): boolean { return this.indexActual <= 0; }
  get esUltimoPaso(): boolean { return this.indexActual === this.pasosDef.length - 1; }

  get enRielDeCondiciones(): boolean { return this.pasoActual === 'condiciones' || this.pasoActual === 'contrato'; }

  get mostrarBotonSiguiente(): boolean {
    return this.pasoActual === 'condiciones' ? !!this.sol?.condiciones : true;
  }
  get textoBotonSiguiente(): string { return this.esUltimoPaso ? 'Ir a decidir' : 'Siguiente'; }

  // ── Navegar entre pasos: guarda lo que cambió antes de moverse ───────────────

  async irAPaso(clave: string): Promise<void> {
    if (this.navegando || clave === this.pasoActual || !this.pasosDef.some((p) => p.clave === clave)) return;
    // Hacia atrás no se le exige nada al paso que se deja: se guarda si se puede y, si no (p. ej. la visita
    // todavía sin comentario), lo escrito queda en la pantalla y se pide recién al avanzar.
    const atras = this.pasosDef.findIndex((p) => p.clave === clave) < this.indexActual;
    this.navegando = true;
    try {
      const ok = await this.guardarPasoActual();
      if (!ok && !atras) return;
      this.pasoActual = clave as ClavePaso;
      this.errorPaso = '';
      void this.router.navigate([], {
        relativeTo: this.route, queryParams: { paso: clave }, queryParamsHandling: 'merge', replaceUrl: true,
      });
    } finally {
      this.navegando = false;
    }
  }

  anterior(): void {
    if (this.esPrimerPaso) return;
    void this.irAPaso(this.pasosDef[this.indexActual - 1].clave);
  }

  siguienteOFinal = async (): Promise<void> => {
    if (this.navegando) return;
    if (!this.esUltimoPaso) { await this.irAPaso(this.pasosDef[this.indexActual + 1].clave); return; }
    this.navegando = true;
    try {
      const ok = await this.guardarPasoActual();
      if (!ok) return;
      this.router.navigate(['/console/altas', this.id], { queryParams: { pestana: 'decision' } });
    } finally {
      this.navegando = false;
    }
  };

  /** Lo que hay que guardar antes de salir de CADA paso (nunca pierde lo escrito: si el servidor
   *  rechaza, el error queda acá, en `errorPaso`, con `formDatos`/`formContactos` intactos). */
  private async guardarPasoActual(): Promise<boolean> {
    this.errorPaso = '';
    if (this.pasoActual === 'datos') return this.guardarDatosSiCambiaron();
    if (this.pasoActual === 'canales') return this.guardarCanalesSiCambiaron();
    if (this.pasoActual === 'contactos') return this.guardarContactosSiValidos();
    if (this.pasoActual === 'cuenta') return this.guardarCuentaSiCambio();
    if (this.pasoActual === 'tienda') return this.guardarTiendaSiCambio();
    if (this.pasoActual === 'visita') return this.comentarVisita();
    return true;   // documentos y contrato (se suben solos) y condiciones (lo guarda el formulario embebido)
  }

  // ── Datos del comercio ────────────────────────────────────────────────────

  private prepararFormDatos(): void {
    const d = this.sol?.datos || {};
    this.formDatos = {};
    for (const campo of CAMPOS_DATOS) (this.formDatos as any)[campo] = (d as any)[campo] ?? '';
    this.formFiscal = fiscalDe(d);
    this.formCentros = [...(d.centros_beneficio || [])];
  }

  tieneCentro(codigo: string): boolean { return this.formCentros.includes(codigo); }

  /** Un centro que el administrador de altas acaba de agregar al catálogo desde el select (o24). */
  alCrearCentro(c: CentroBeneficio): void {
    if (!this.centros.some((x) => x.codigo === c.codigo)) this.centros = [...this.centros, c];
  }


  private datosCambiados(): DatosAlta {
    const originales = this.sol?.datos || {};
    const cambios: DatosAlta = {};
    for (const campo of CAMPOS_DATOS) {
      const antes = ((originales as any)[campo] ?? '').toString().trim();
      const ahora = ((this.formDatos as any)[campo] ?? '').toString().trim();
      if (ahora !== antes) (cambios as any)[campo] = (this.formDatos as any)[campo];
    }
    const fiscalAntes = fiscalDe(originales);
    if (PIEZAS_FISCAL.some((p) => String(this.formFiscal[p.clave] || '').trim() !== fiscalAntes[p.clave].trim())) cambios.fiscal = fiscalSinVacios(this.formFiscal);
    const centrosAntes = [...(originales.centros_beneficio || [])].sort().join(',');
    if ([...this.formCentros].sort().join(',') !== centrosAntes) cambios.centros_beneficio = [...this.formCentros];
    return cambios;
  }

  private async guardarDatosSiCambiaron(): Promise<boolean> {
    if (telefonoInvalido(this.formDatos.telefono)) {
      this.errorPaso = `El teléfono del comercio no es válido: ${AYUDA_TELEFONO}`;
      return false;
    }
    const cambios = this.datosCambiados();
    if (!Object.keys(cambios).length) return true;
    this.guardandoDatos = true;
    try {
      this.sol = await this._sa.actualizar(this.id, { datos: cambios });
      this.prepararFormDatos();
      return true;
    } catch (e: any) {
      if (sinCambios(e)) return true;
      this.errorPaso = mensajeDeError(e, 'No se pudieron guardar los datos.');
      return false;
    } finally {
      this.guardandoDatos = false;
    }
  }

  // ── Canales de venta ──────────────────────────────────────────────────────

  private prepararFormCanales(): void { this.formCanales = normalizarCanales(this.sol?.canales); }

  private async guardarCanalesSiCambiaron(): Promise<boolean> {
    const antes = normalizarCanales(this.sol?.canales).join(',');
    if (this.formCanales.join(',') === antes && (this.sol?.canales || []).length) return true;
    this.guardandoCanales = true;
    try {
      this.sol = await this._sa.guardarCanales(this.id, this.formCanales);
      this.prepararFormCanales();
      return true;
    } catch (e: any) {
      if (sinCambios(e)) return true;
      this.errorPaso = mensajeDeError(e, 'No se pudieron guardar los canales de venta.');
      return false;
    } finally {
      this.guardandoCanales = false;
    }
  }

  // ── Contactos ─────────────────────────────────────────────────────────────
  // Vocabulario 2026-09-29: «Pagos» se llama «Finanzas», y un contacto puede tener VARIOS papeles
  // a la vez (checkboxes, no un select). Administrador y Finanzas son obligatorios por igual;
  // Técnico es un aviso aparte (depende de las condiciones, que acá todavía no se saben).

  nombrePapel(p: PapelContacto): string { return PAPELES.find((x) => x.codigo === p)?.nombre || p; }
  /** Expuesto tal cual para la plantilla: un solo lugar lee `papel`/`papeles` (service.ts). */
  papelesDe = papelesDe;

  private prepararFormContactos(): void {
    this.formContactos = this.contactosGuardados();
  }

  /** Los contactos de la solicitud con la forma del formulario: así se sabe si se tocó algo. */
  private contactosGuardados() {
    return (this.sol?.contactos || []).map((c) => ({
      nombre: c.nombre, correo: c.correo, telefono: c.telefono, whatsapp: c.whatsapp,
      papeles: papelesDe(c),
    }));
  }

  agregarContacto(): void {
    this.formContactos = [...this.formContactos, { papeles: [], nombre: '', correo: '', telefono: '' }];
  }

  quitarContacto(i: number): void { this.formContactos = this.formContactos.filter((_, k) => k !== i); }

  tienePapel(c: ContactoAlta, p: PapelContacto): boolean { return (c.papeles || []).includes(p); }

  togglePapel(c: ContactoAlta, p: PapelContacto): void {
    const actuales = c.papeles || [];
    c.papeles = actuales.includes(p) ? actuales.filter((x) => x !== p) : [...actuales, p];
  }

  /** «Faltan: el contacto administrador.» — `null` si no falta. */
  get faltanContactos(): string | null { return textoFaltanContactos(this.formContactos, this.formCanales); }
  /** «Obligatorio: el comercio vende por Integración por API» — null si los canales marcados no piden técnico. */
  get tecnicoObligatorio(): string | null { return textoTecnicoObligatorio(this.formCanales); }
  get vendedorObligatorio(): boolean { return this.formCanales.includes('LINK_PAGO'); }

  /** El administrador es obligatorio (el de finanzas no, desde el 2026-10-14): hace falta al menos
   *  un contacto administrador, además de que todos tengan nombre. */
  get contactosValidos(): boolean {
    return this.formContactos.length > 0
      && this.formContactos.every((c) => c.nombre && c.nombre.trim().length > 0)
      && !this.faltanContactos;
  }

  private async guardarContactosSiValidos(): Promise<boolean> {
    if (!this.contactosValidos) {
      // ALTA-24: se dice lo que de verdad falta (el técnico, el administrador o un nombre), no las tres cosas a la vez.
      const sinNombre = this.formContactos.some((c) => !c.nombre || !c.nombre.trim());
      this.errorPaso = this.faltanContactos || (sinNombre ? 'A un contacto le falta el nombre.' : 'Falta al menos un contacto.');
      return false;
    }
    const malTel = this.formContactos.find((c) => telefonoInvalido(c.telefono));
    if (malTel) {
      this.errorPaso = `El teléfono de ${malTel.nombre || 'un contacto'} no es válido: ${AYUDA_TELEFONO}`;
      return false;
    }
    // ALTA-32: sin cambios no se manda nada (el servidor respondería 400 «No cambió nada»).
    if (JSON.stringify(this.formContactos) === JSON.stringify(this.contactosGuardados())) return true;
    this.guardandoContactos = true;
    try {
      this.sol = await this._sa.actualizar(this.id, { contactos: this.formContactos });
      this.prepararFormContactos();
      return true;
    } catch (e: any) {
      // Adrián (p1177): «Siguiente» sin tocar nada no es un error — el servidor avisa «No cambió nada» y se avanza.
      if (sinCambios(e)) return true;
      this.errorPaso = mensajeDeError(e, 'No se pudieron guardar los contactos.');
      return false;
    } finally {
      this.guardandoContactos = false;
    }
  }

  // ── Cuenta bancaria ───────────────────────────────────────────────────────

  /** El botón «Siguiente» nunca frena acá (el FRENA es al aprobar), salvo un número a medio
   *  escribir: eso sí se avisa, para no guardar algo que el servidor va a rechazar igual. */
  private async guardarCuentaSiCambio(): Promise<boolean> {
    const c = this.cuentaActual;
    // Sin número no hay cuenta: el titular y el RIF vienen precargados del comercio y solos no cuentan.
    const vacia = !c || !c.numero;
    if (vacia) return true;
    if (!/^\d{20}$/.test(c!.numero || '')) {
      this.errorPaso = 'El número de cuenta son 20 dígitos.';
      return false;
    }
    if (JSON.stringify(c) === JSON.stringify(this.sol?.datos.cuenta || null)) return true;
    this.guardandoCuenta = true;
    try {
      this.sol = await this._sa.actualizar(this.id, { datos: { cuenta: c! } });
      this.cuentaActual = this.sol.datos.cuenta || null;
      return true;
    } catch (e: any) {
      if (sinCambios(e)) return true;
      this.errorPaso = mensajeDeError(e, 'No se pudo guardar la cuenta bancaria.');
      return false;
    } finally {
      this.guardandoCuenta = false;
    }
  }

  // ── Documentos ────────────────────────────────────────────────────────────

  get puedeSubirDocumentos(): boolean { return !!this.sol?.secciones_editables.includes('DOCUMENTOS'); }

  urlDocumento(url: string): string { return this._sa.urlDocumento(url); }

  elegirArchivoDocumento(d: DocumentoSolicitud, event: Event): void {
    const input = event.target as HTMLInputElement;
    const archivo = input.files && input.files[0];
    input.value = '';
    if (!archivo) return;
    // ALTA-40: lo que se elige mientras otro sube queda en cola y sube después (antes se perdía sin aviso).
    this.enCola = [...this.enCola.filter((x) => x.tipo !== d.codigo), { tipo: d.codigo, archivo }];
    void this.subirLaCola();
  }

  /** Los documentos elegidos que esperan su turno para subir, uno a la vez. */
  enCola: Array<{ tipo: string; archivo: File }> = [];
  enColaTipo = (tipo: string): boolean => this.enCola.some((x) => x.tipo === tipo);

  private subirLaCola = async (): Promise<void> => {
    if (this.subiendoTipo) return;
    this.errorPaso = '';
    while (this.enCola.length) {
      const [{ tipo, archivo }, ...resto] = this.enCola;
      this.enCola = resto;
      this.subiendoTipo = tipo;
      try {
        this.sol = await this._sa.subirDocumento(this.id, tipo, archivo);
      } catch (e: any) {
        this.errorPaso = mensajeDeError(e, 'No se pudo cargar el documento.');
      } finally {
        this.subiendoTipo = null;
      }
    }
  };

  /** Minuta del 5-oct; Juan, 2026-10-08: «que puedas eliminar un documento y volverlo a cargar». */
  /** Juan, 2026-10-08: las acciones de un documento cargado van en el «⋯»; Quitar se confirma ahí mismo. */
  readonly opcionesDocumento: OpcionMenu[] = [
    { clave: 'ver', texto: 'Ver el archivo', icono: 'fa-external-link-alt' },
    { clave: 'reemplazar', texto: 'Reemplazar', icono: 'fa-file-upload', tip: 'Sube otro archivo en lugar de este' },
    { clave: 'quitar', texto: 'Quitar', icono: 'fa-trash-alt', peligro: true, separar: true, confirmar: 'Sí, quitarlo',
      tip: 'Lo borra de la solicitud; después puedes subir otro' },
  ];

  menuDocumento(d: DocumentoSolicitud, clave: string, input: HTMLInputElement): void {
    if (clave === 'ver' && d.cargado) window.open(this.urlDocumento(d.cargado.url), '_blank', 'noopener');
    else if (clave === 'reemplazar') input.click();
    else if (clave === 'quitar') this.quitarDocumento(d.codigo);
  }

  quitarDocumento = async (tipo: string): Promise<void> => {
    this.quitandoTipo = tipo; this.errorPaso = '';
    try {
      this.sol = await this._sa.quitarDocumento(this.id, tipo);
    } catch (e: any) {
      this.errorPaso = mensajeDeError(e, 'No se pudo quitar el documento.');
    } finally {
      this.quitandoTipo = null;
    }
  };

  // ── La visita ─────────────────────────────────────────────────────────────

  private comentarVisita = async (): Promise<boolean> => {
    const texto = this.comentarioVisita.trim();
    if (!texto) {
      if (this.hayComentarioVisita) return true;   // ya hay uno de antes: se puede saltar
      this.errorPaso = 'Escribe qué viste en la visita: sin ese comentario no se puede decidir.';
      return false;
    }
    this.comentando = true;
    try {
      await this._sa.comentar(this.id, texto);
      this.comentarioVisita = '';
      this.hayComentarioVisita = true;
      return true;
    } catch (e: any) {
      this.errorPaso = mensajeDeError(e, 'No se pudo agregar el comentario.');
      return false;
    } finally {
      this.comentando = false;
    }
  };

  // ── Condiciones: `app-condiciones-pasos` en modo 'propuesta' ─────────────────────────────────────

  reabrirCondiciones(): void { this.editandoCondicionesFallback = true; this.errorCondiciones = ''; }

  /** `?paso=plazo|cobros|modelos|riesgo|revisar` abre ese paso de las condiciones (lo usa «Completar» del Expediente). */
  get pasoCondiciones(): string | null {
    const p = this.route.snapshot.queryParamMap.get('paso') || '';
    return ['plazo', 'cobros', 'modelos', 'riesgo', 'revisar'].includes(p) ? p : null;
  }

  guardarCondicionesEmbebidas = async (cuerpo: any): Promise<void> => {
    this.guardandoCondiciones = true; this.errorCondiciones = '';
    try {
      this.sol = await this._sa.guardarCondiciones(this.id, cuerpo);
      this.editandoCondicionesFallback = false;
      // Juan, 2026-10-08: guardar es el final del riel; de vuelta a la solicitud, a decidir.
      this.router.navigate(['/console/altas', this.id]);
    } catch (e: any) {
      this.errorCondiciones = mensajeDeError(e, 'No se pudieron guardar las condiciones.');
    } finally {
      this.guardandoCondiciones = false;
    }
  };

  cancelarCondicionesEmbebidas(): void {
    if (this.sol?.condiciones) { this.editandoCondicionesFallback = false; return; }
    this.router.navigate(['/console/altas', this.id]);
  }

  // ── Tienda y caja (Juan, 2026-10-08): la primera tienda y su caja las define el ejecutivo ──────────

  formTienda: FormTienda = tiendaVacia('');
  guardandoTienda = false;

  /** El correo escrito no tiene forma de correo (para el aviso bajo el campo; vacío no avisa: eso lo dice «Siguiente»). */
  /** ALTA-29: la misma regla que el núcleo (entero entre 1 y 9999), avisada en el campo y no al guardar. */
  cajaMala = (c: any): boolean => c === null || c === undefined || c === '' || !Number.isInteger(Number(c)) || Number(c) < 1 || Number(c) > 9999;
  readonly MSG_CAJA = MSG_CAJA;

  correoMalo = (c: string | undefined): boolean => !!String(c || '').trim() && !CORREO.test(String(c).trim());

  /**
   * Los usuarios del portal que se le proponen a la caja y al gerente (Juan, 2026-10-19): `<código>.caja<N>` y
   * `<código>.gerente`, como los arma el núcleo (`usuariosPropuestos`). Es la propuesta: el servidor decide.
   */
  /** Antes de crear el comercio `codigo_previsto` son sólo las siglas: el correlativo se marca «•••••••». */
  private get prefijoUsuario(): string {
    const c = String(this.sol?.codigo_previsto || '').trim().toLowerCase();
    return /^[a-z]{2,5}$/.test(c) ? `${c}•••••••` : c;
  }
  get usuarioCajaPropuesto(): string {
    const c = this.prefijoUsuario;
    const n = Number(this.formTienda.caja);
    return c ? `${c}.caja${Number.isInteger(n) && n >= 1 ? n : 1}` : '';
  }
  get usuarioGerentePropuesto(): string {
    const c = this.prefijoUsuario;
    return c ? `${c}.gerente` : '';
  }
  get usuarioVendedorPropuesto(): string {
    const c = this.prefijoUsuario;
    return c ? `${c}.vendedor` : '';
  }
  get usuarioPortalPropuesto(): string {
    const c = this.prefijoUsuario;
    return c ? `${c}.portal` : '';
  }

  /**
   * ALTA-31: si el código es la propuesta sin tocar, al aprobar se le pone el código completo del comercio
   * (`tiendaDe` en el núcleo: CRT-01 → CRT0000007-01). Esto es lo que va a quedar; '' si queda tal cual.
   */
  get codigoTiendaAlNacer(): string {
    const codigo = String(this.formTienda.codigo || '').trim().toUpperCase();
    const propuesto = this.codigoTiendaPropuesto();
    if (!propuesto || codigo !== propuesto) return '';
    const m = /^(.+)-(\d+)$/.exec(codigo);
    return m && /^[A-Z]{2,5}$/.test(m[1]) ? `${m[1]}•••••••-${m[2]}` : '';
  }

  /** `<código del comercio>-01`, o '' si el servidor no manda `codigo_previsto`. */
  private codigoTiendaPropuesto(): string {
    const p = String(this.sol?.codigo_previsto || '').trim().toUpperCase();
    return p ? `${p}-01` : '';
  }

  private prepararFormTienda(): void {
    const t = this.sol?.datos.tienda;
    const propuesto = this.codigoTiendaPropuesto();
    this.formTienda = t
      ? {
        codigo: t.codigo || propuesto, nombre: t.nombre, nombre_corto: t.nombre_corto || '', caja: t.caja, direccion: t.direccion || '', ciudad: t.ciudad || '',
        gerente: { nombre: t.gerente?.nombre || '', correo: t.gerente?.correo || '' },
        vendedor: { nombre: t.vendedor?.nombre || '', correo: t.vendedor?.correo || '' },
      }
      : tiendaVacia(propuesto);   // Adrián (p1178): lo que no se sabe no se inventa (ni «<comercio> principal» ni el domicilio fiscal)
  }

  private async guardarTiendaSiCambio(): Promise<boolean> {
    const f = this.formTienda;
    const t: TiendaInicial = {
      codigo: String(f.codigo || '').trim().toUpperCase(), nombre: String(f.nombre || '').trim(),
      nombre_corto: String(f.nombre_corto || '').trim(),
      caja: f.caja === null || (f.caja as any) === '' ? null : Number(f.caja),
      direccion: String(f.direccion || '').trim() || undefined, ciudad: String(f.ciudad || '').trim() || undefined,
    };
    const persona = (p: PersonaTienda): PersonaTienda => ({ nombre: String(p.nombre || '').trim(), correo: String(p.correo || '').trim().toLowerCase() });
    t.gerente = persona(f.gerente);
    const v = persona(f.vendedor);
    t.vendedor = v.nombre || v.correo ? v : null;   // opcional: sin nada escrito no viaja
    const propuesto = this.codigoTiendaPropuesto();
    t.codigo_sugerido = !!propuesto && t.codigo === propuesto;
    const nadie = !t.gerente.nombre && !t.gerente.correo;
    if (!t.codigo && !t.nombre && !t.nombre_corto && nadie) return true;   // sin nada escrito no frena moverse; el requisito frena al aprobar
    if (!t.codigo) { this.errorPaso = 'Falta el código de la tienda.'; return false; }
    if (!t.nombre) { this.errorPaso = 'Falta el nombre de la tienda.'; return false; }
    if (!t.nombre_corto) { this.errorPaso = 'Falta el nombre corto de la tienda.'; return false; }
    if (this.cajaMala(t.caja)) { this.errorPaso = MSG_CAJA; return false; }
    // Juan, 2026-10-19: el gerente es obligatorio (la caja es un usuario, no una persona). Mismos textos que el núcleo.
    const g = t.gerente;
    if (!g.nombre) { this.errorPaso = 'Falta el nombre del gerente.'; return false; }
    if (!g.correo) { this.errorPaso = 'Falta el correo del gerente.'; return false; }
    if (!CORREO.test(g.correo)) { this.errorPaso = 'El correo del gerente no es válido: escriba uno con @, por ejemplo nombre@dominio.com.'; return false; }
    // El vendedor es opcional, pero si se escribe algo va completo (la regla de «obligatorio» con link de pago es del núcleo).
    if (t.vendedor) {
      if (!t.vendedor.nombre) { this.errorPaso = 'Falta el nombre del vendedor.'; return false; }
      if (!t.vendedor.correo) { this.errorPaso = 'Falta el correo del vendedor.'; return false; }
      if (!CORREO.test(t.vendedor.correo)) { this.errorPaso = 'El correo del vendedor no es válido: escriba uno con @, por ejemplo nombre@dominio.com.'; return false; }
    }
    const antes = this.sol?.datos.tienda;
    const igual = (a: PersonaTienda | null | undefined, b: PersonaTienda) => !!a && a.nombre === b.nombre && a.correo === b.correo;
    if (antes && antes.codigo === t.codigo && antes.nombre === t.nombre && (antes.nombre_corto || '') === t.nombre_corto && antes.caja === t.caja
      && (antes.direccion || undefined) === t.direccion && (antes.ciudad || undefined) === t.ciudad
      && igual(antes.gerente, t.gerente)
      && (t.vendedor ? igual(antes.vendedor, t.vendedor) : !antes.vendedor?.nombre && !antes.vendedor?.correo)) return true;
    this.guardandoTienda = true;
    try {
      this.sol = await this._sa.actualizar(this.id, { datos: { tienda: t } });
      this.prepararFormTienda();
      return true;
    } catch (e: any) {
      if (sinCambios(e)) return true;
      this.errorPaso = mensajeDeError(e, 'No se pudo guardar la tienda.');
      return false;
    } finally {
      this.guardandoTienda = false;
    }
  }
}

// El mismo que valida el núcleo (`dominio/tienda-inicial.js`).
const CORREO = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** El formulario de «Tienda y caja»: la tienda y, siempre presentes, su gerente y su vendedor. */
type FormTienda = TiendaInicial & { gerente: PersonaTienda; vendedor: PersonaTienda };

function tiendaVacia(codigo: string): FormTienda {
  return { codigo, nombre: '', nombre_corto: '', caja: 1, direccion: '', ciudad: '', gerente: { nombre: '', correo: '' }, vendedor: { nombre: '', correo: '' } };
}
